import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, Inbox, AlertTriangle, User, MailOpen, RefreshCw, MessagesSquare } from "lucide-react";
import AdminLayout from "../../admin/AdminLayout";
import AdminAuthGuard from "../../admin/AdminAuthGuard";
import PriorityPill from "../../components/PriorityPill";
import SentimentDot from "../../components/SentimentDot";
import SLATimer from "../../components/SLATimer";
import SendbirdChannelPanel from "../../components/sendbird/SendbirdChannelPanel";
import { api } from "../../api/client";

const STATUS_LABEL = {
    open: "Open",
    awaiting_agent: "Awaiting agent",
    awaiting_customer: "Awaiting customer",
    resolved: "Resolved",
    closed: "Closed",
};

const STATUS_TONE = {
    open: "bg-emerald-100 text-emerald-700",
    awaiting_agent: "bg-blue-100 text-blue-700",
    awaiting_customer: "bg-amber-100 text-amber-800",
    resolved: "bg-zinc-100 text-zinc-700",
    closed: "bg-zinc-200 text-zinc-600",
};

const AdminSupportContent = () => {
    const [params, setParams] = useSearchParams();
    const [data, setData] = useState({ items: [], total: 0, counts: {} });
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState(params.get("q") || "");
    const [section, setSection] = useState(params.get("section") === "chats" ? "chats" : "queue");

    const filter = {
        status: params.get("status") || "",
        priority: params.get("priority") || "",
        assigned: params.get("assigned") || "",
        sentiment: params.get("sentiment") || "",
        sla: params.get("sla") || "",
        q: params.get("q") || "",
    };

    const setF = (k, v) => {
        const p = new URLSearchParams(params);
        if (v) p.set(k, v); else p.delete(k);
        p.delete("page");
        setParams(p);
    };

    useEffect(() => {
        let mounted = true;
        const load = async () => {
            setLoading(true);
            try {
                const { data } = await api.get("/admin/support/tickets", { params: { ...filter, page_size: 25 } });
                if (mounted) setData(data);
            } finally {
                if (mounted) setLoading(false);
            }
        };
        load();
        return () => { mounted = false; };
    }, [filter.status, filter.priority, filter.assigned, filter.sentiment, filter.sla, filter.q]); // eslint-disable-line

    const counts = data.counts || {};
    const chips = [
        { key: null, label: "All open", count: counts.open || 0, val: () => { setF("assigned", ""); setF("sla", ""); setF("status", ""); } },
        { key: "assigned=me", label: "My tickets", count: counts.my_tickets || 0, val: () => setF("assigned", "me") },
        { key: "assigned=unassigned", label: "Unassigned", count: counts.unassigned || 0, val: () => setF("assigned", "unassigned") },
        { key: "priority=urgent", label: "Urgent", count: counts.urgent || 0, val: () => setF("priority", "urgent") },
        { key: "sla=breached", label: "SLA breached", count: counts.sla_breached || 0, val: () => setF("sla", "breached") },
    ];

    const currentKey =
        filter.assigned === "me" ? "assigned=me"
            : filter.assigned === "unassigned" ? "assigned=unassigned"
                : filter.priority === "urgent" ? "priority=urgent"
                    : filter.sla === "breached" ? "sla=breached"
                        : null;

    return (
        <div className="space-y-4">
            {/* Section tabs — Queue vs. Live Chats */}
            <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1 shadow-sm" data-testid="admin-support-section-tabs">
                <button
                    onClick={() => setSection("queue")}
                    className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                        section === "queue" ? "bg-brand-700 text-white shadow-sm" : "text-slate-600 hover:bg-slate-50"
                    }`}
                    data-testid="support-queue-tab"
                >
                    <Inbox className="h-3.5 w-3.5" /> Ticket Queue
                </button>
                <button
                    onClick={() => setSection("chats")}
                    className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                        section === "chats" ? "bg-brand-700 text-white shadow-sm" : "text-slate-600 hover:bg-slate-50"
                    }`}
                    data-testid="support-live-chats-tab"
                >
                    <MessagesSquare className="h-3.5 w-3.5" /> Live Support Chats
                </button>
            </div>

            {section === "chats" ? (
                <SendbirdChannelPanel
                    customType="support_ticket"
                    claimEndpoint={(ch) => {
                        const parts = (ch?.url || "").split("_");
                        const ticketId = parts.slice(2).join("_");
                        return `/sendbird/channels/support/${ticketId}/claim`;
                    }}
                    claimButtonLabel="Claim ticket"
                    testId="support"
                    height="calc(100vh - 14rem)"
                />
            ) : (
            <>
            <div className="flex flex-wrap items-center gap-2">
                {chips.map((c) => (
                    <button
                        key={c.label}
                        onClick={c.val}
                        className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-all ${
                            currentKey === c.key
                                ? "border-brand-300 bg-brand-700 text-white"
                                : "border-slate-200 bg-white text-slate-700 hover:border-brand-200"
                        }`}
                        data-testid={`admin-support-chip-${c.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    >
                        {c.label}
                        <span className={`rounded-full px-1.5 text-[10px] font-bold ${currentKey === c.key ? "bg-white/30 text-white" : "bg-slate-100 text-slate-600"}`}>
                            {c.count}
                        </span>
                    </button>
                ))}

                <form
                    onSubmit={(e) => { e.preventDefault(); setF("q", search.trim()); }}
                    className="relative ml-auto flex w-full max-w-xs items-center md:w-auto"
                >
                    <Search className="pointer-events-none absolute left-3 h-3.5 w-3.5 text-slate-400" />
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search tickets, customers, subjects…"
                        className="w-full rounded-full border border-slate-200 bg-white pl-9 pr-3 py-1.5 text-xs outline-none focus:border-brand-400"
                        data-testid="admin-support-search-input"
                    />
                </form>
            </div>

            {loading ? (
                <div className="space-y-2">
                    {[1, 2, 3, 4].map((i) => <div key={i} className="h-16 skeleton rounded-xl" />)}
                </div>
            ) : data.items.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-12 text-center" data-testid="admin-support-empty">
                    <Inbox className="mx-auto mb-2 h-10 w-10 text-slate-300" />
                    <p className="text-sm font-medium text-slate-700">No tickets match these filters.</p>
                </div>
            ) : (
                <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
                    <table className="w-full text-sm">
                        <thead className="border-b border-slate-100 bg-slate-50 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                            <tr>
                                <th className="px-4 py-3">Ticket</th>
                                <th className="px-4 py-3">Customer</th>
                                <th className="px-4 py-3">Priority</th>
                                <th className="px-4 py-3">Status</th>
                                <th className="px-4 py-3">Sentiment</th>
                                <th className="px-4 py-3">Assigned</th>
                                <th className="px-4 py-3">SLA</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                            {data.items.map((t) => (
                                <tr key={t.id} className="hover:bg-slate-50" data-testid={`admin-support-row-${t.id}`}>
                                    <td className="px-4 py-3">
                                        <Link to={`/admin/support/${t.id}`} className="block max-w-[260px]">
                                            <p className="text-xs font-mono text-slate-400">{t.ticket_no}</p>
                                            <p className="line-clamp-1 text-sm font-medium text-slate-900">{t.subject}</p>
                                        </Link>
                                    </td>
                                    <td className="px-4 py-3 text-xs text-slate-700">
                                        <p>{t.user_name}</p>
                                        <p className="text-slate-400">{t.user_email}</p>
                                    </td>
                                    <td className="px-4 py-3"><PriorityPill priority={t.priority} /></td>
                                    <td className="px-4 py-3">
                                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${STATUS_TONE[t.status] || ""}`}>
                                            {STATUS_LABEL[t.status] || t.status}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3"><SentimentDot sentiment={t.sentiment} /></td>
                                    <td className="px-4 py-3 text-xs text-slate-600">
                                        {t.assigned_to?.full_name || t.assigned_to?.email || <span className="text-slate-400">—</span>}
                                    </td>
                                    <td className="px-4 py-3"><SLATimer slaDueAt={t.sla_due_at} slaBreached={t.sla_breached} status={t.status} /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            </>
            )}
        </div>
    );
};

const AdminSupport = () => (
    <AdminAuthGuard>
        <AdminLayout title="Support" subtitle="Customer service tickets">
            <AdminSupportContent />
        </AdminLayout>
    </AdminAuthGuard>
);

export default AdminSupport;
